/**
 * AdminRitualCard Component
 * Displays a ritual with stats and actions for admins
 * Used in AdminRitualTab for community admins/owners
 */

import {
  Archive,
  ArchiveRestore,
  Banknote,
  CircleCheck,
  EllipsisVertical,
  Eye,
  Loader2,
  MessageCircleMore,
  Upload,
  Users,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { ShareModal } from "@/components/shared/ShareModal"
import { Card } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useArchiveRitual, useUnarchiveRitual } from "@/features/rituals/hooks/useArchiveRitual"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { cn } from "@/lib"

export interface AdminRitualCardProps {
  id: string
  slug?: string
  image?: string
  title: string
  type: string
  price?: number
  participationFee?: "Free" | "Paid" | "Donation-based"
  dateTime?: string
  duration: string
  location?: string
  viewsCount?: number
  joinedCount?: number
  completedCount?: number
  earnings?: number
  chatCount?: number
  isArchived?: boolean
  communityId: string
}

export function AdminRitualCard({
  id,
  slug,
  image,
  title,
  type,
  price,
  participationFee,
  dateTime,
  duration,
  location,
  viewsCount = 0,
  joinedCount = 0,
  completedCount = 0,
  earnings = 0,
  chatCount = 0,
  isArchived = false,
  communityId,
}: AdminRitualCardProps) {
  const navigate = useNavigate()
  const { archive, isArchiving } = useArchiveRitual(communityId)
  const { unarchive, isUnarchiving } = useUnarchiveRitual(communityId)

  const isLoading = isArchiving || isUnarchiving
  const [shareOpen, setShareOpen] = useState(false)

  // Format number with k suffix for large numbers
  const formatCount = (count: number): string => {
    if (count >= 1000) {
      return `${Math.floor(count / 1000)}k`
    }
    return count.toString()
  }

  // Format earnings with $ prefix
  const formatEarnings = (amount: number): string => {
    return `$${amount.toLocaleString()}`
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation()
    archive(id, {
      onSuccess: () => {
        toast.success(
          isArchived ? "Ritual unarchived successfully" : "Ritual archived successfully",
        )
      },
      onError: error => {
        toast.error(error instanceof Error ? error.message : "Failed to archive ritual")
      },
    })
  }

  const handleUnarchive = (e: React.MouseEvent) => {
    e.stopPropagation()
    unarchive(id, {
      onSuccess: () => {
        toast.success("Ritual unarchived successfully")
      },
      onError: error => {
        toast.error(error instanceof Error ? error.message : "Failed to unarchive ritual")
      },
    })
  }

  return (
    <>
      <Card
        className="overflow-hidden h-full w-full border-0 bg-beige-medium p-0 cursor-pointer rounded-tl-3xl rounded-tr-none rounded-bl-none rounded-br-3xl border border-white/15"
        onClick={() => navigate(`/rituals/${id}`)}
      >
        <div>
          <div className="relative aspect-[647/300] w-full overflow-hidden rounded-br-3xl">
            {/* Cover Image */}
            {image ? (
              <img
                src={image}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover rounded-br-3xl"
              />
            ) : null}

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.4)] via-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0.7)]" />

            {/* Content */}
            <div className=" flex flex-col justify-between p-5">
              {/* Top Section - Action Icons */}
              <div className=" relative z-10 flex items-start justify-end gap-3">
                {/* Chat Icon with count badge */}
                <div className="relative p-3 rounded-lg border-gradient-glass">
                  <MessageCircleMore className="w-5 h-5 text-white" />
                  {chatCount > 0 && (
                    <span className="absolute -top-2 -right-3 bg-brand-terracotta text-white text-[10px] font-medium rounded-full min-w-[25px] h-[25px] flex items-center justify-center px-1">
                      {formatCount(chatCount)}
                    </span>
                  )}
                </div>
                {/* Share Icon */}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    setShareOpen(true)
                  }}
                  className="p-3 rounded-lg border-gradient-glass cursor-pointer"
                >
                  <Upload className="w-5 h-5 text-white" />
                </button>
                {/* More/Hamburger Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      onClick={e => e.stopPropagation()}
                      className="p-3 rounded-lg border-gradient-glass cursor-pointer hover:bg-white/20 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      ) : (
                        <EllipsisVertical className="w-5 h-5 text-white" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {isArchived ? (
                      <DropdownMenuItem
                        onClick={handleUnarchive}
                        className="cursor-pointer flex items-center gap-2"
                      >
                        <ArchiveRestore className="w-4 h-4" />
                        Unarchive
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={handleArchive}
                        className="cursor-pointer flex items-center gap-2"
                      >
                        <Archive className="w-4 h-4" />
                        Archive
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Bottom Section - Title and Metadata */}
          <div className="flex flex-col gap-2 py-6 px-5 z-40">
            {/* Title */}
            <h3 className="line-clamp-2 text-[18px] font-bold leading-snug text-white">{title}</h3>

            {/* Metadata Row */}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {/* Price Badge */}
              <span
                className={cn(
                  "text-white rounded px-2 py-1 text-[13px]",
                  participationFee === "Free"
                    ? "bg-olive"
                    : participationFee === "Donation-based"
                      ? "bg-brand-terracotta"
                      : "bg-[#C86757]",
                )}
              >
                {participationFee === "Donation-based"
                  ? "Donation-based"
                  : participationFee === "Paid"
                    ? `$${price}`
                    : "Free"}
              </span>

              {/* Type */}
              <span className="text-white rounded  py-1 text-[13px]">{type}</span>

              {/* Date/Time */}
              {dateTime && (
                <span className="flex items-center gap-1 text-white/80 text-[13px]">
                  {dateTime}
                </span>
              )}

              {/* Duration */}
              {duration && (
                <span className="text-[13px] text-white/80">{formatDuration(duration)}</span>
              )}

              {/* Location */}
              {location && participationFee !== "Free" && (
                <span className="flex items-center gap-1 text-[13px] text-white/80 truncate overflow-hidden">
                  @{location}
                </span>
              )}
            </div>
          </div>

          {/* Stats Row */}
          <div className="hidden md:flex font-normal items-center gap-4 px-4 py-4 bg-beige-soft border-t border-stone/20 text-[14px]">
            <span>Views . {formatCount(viewsCount)}</span>
            <span>Joined . {joinedCount}</span>
            <span>Completed . {completedCount}</span>
            {participationFee !== "Free" && <span>Earnings . {formatEarnings(earnings)}</span>}
          </div>
          {/* Stats Row: Mobile */}
          <div className="md:hidden flex items-center gap-7.5 p-3.75 bg-beige-soft border-t border-stone/20 text-sm">
            <span className="flex items-center gap-2.5 text-stone">
              <Eye className="w-5 h-5" />
              {formatCount(viewsCount)}
            </span>
            <span className="flex items-center gap-2.5 text-stone">
              <Users className="w-5 h-5" />
              {joinedCount}
            </span>
            <span className="flex items-center gap-2.5 text-stone">
              <CircleCheck className="w-5 h-5" />
              {completedCount}
            </span>
            {participationFee !== "Free" && (
              <span className="flex items-center gap-2.5 text-stone">
                <Banknote className="w-5 h-5" />
                {formatEarnings(earnings)}
              </span>
            )}
          </div>
        </div>
      </Card>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/rituals/${slug || id}`}
        shareText={`Check out this ritual: ${title}`}
        shareType="ritual"
        shareImageUrl={image || null}
        displayPath={`/rituals/${title.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </>
  )
}
